/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import React, { useEffect, useState } from "react";
import { Image, Button, Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { ExternalLinkIcon } from "@heroicons/react/solid";
import * as S from "./styles";
import { StorageModule } from "@vlinder-web/storage-module-react";
import { isMobile } from "react-device-detect";

export interface IHeroSectionProps {
  heroSectionId?: string;
  heroHeading1?: string;
  heroHeading2?: string;
  heroHeadingBadge?: string;
  heroDescription?: string;
  heroDarkButtonItem?: {
    name: string;
    link: string;
  };
  heroLightButtonItem?: {
    name: string;
    link: string;
  };
  heroBrand?: {
    name: string;
    logo: string;
    link: string;
    footerText?: string;
    height?: number;
    width?: number;
  };
  heroWatermark?: {
    name?: string;
    image: string;
    height?: number;
    width?: number;
  };
  heroOrg?: {
    name?: string;
    image: string;
    height?: number;
    width?: number;
  };
  buttonStyle?: {
    bgColor?: string;
    textColor?: string;
    hoverBgColor?: string;
    hoverTextColor?: string;
  };
}

export const HeroSection = ({
  heroSectionId,
  heroHeading1,
  heroHeading2,
  heroHeadingBadge,
  heroDescription,
  heroDarkButtonItem,
  heroLightButtonItem,
  heroBrand,
  heroWatermark,
  buttonStyle,
  heroOrg,
}: IHeroSectionProps) => {
  const storageSrv = StorageModule?.getInstance();
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768); // or any mobile breakpoint
    };

    handleResize(); // initial check
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const _renderHeadingIf = () => {
    if (heroHeading1) {
      return (
        <S.Heading className="fw-bolder text-secondary">
          {heroHeading1}
          {heroHeading2 && (
            <span className="d-none d-sm-inline">{` ${heroHeading2}`}</span>
          )}
          {heroHeadingBadge && (
            <span
              className="pro-badge fw-bolder"
              style={{ textShadow: "none" }}
            >
              {heroHeadingBadge}
            </span>
          )}
        </S.Heading>
      );
    }
  };
  const _renderDescriptionIf = () => {
    if (heroDescription) {
      return (
        <S.Description className="fw-light mb-5 h5">
          {heroDescription}
        </S.Description>
      );
    }
  };
  const _renderDescriptionButtonsIf = () => {
    return (
      <div
        className="d-flex justify-content-center"
        style={{
          position: "absolute",
          // border: "11px solid red",
          bottom: 0,
          marginBottom: isMobileView ? "180px" : "20px", // top:100,
          // paddingTop: "13rem",
          left: isMobileView ? "26%" : "42%",
          right: isMobileView ? "26%" : "42%",
        }}
      >
        {heroDarkButtonItem && (
          <S.GButton
            variant="secondary"
            // as={Link}
            // to={heroDarkButtonItem?.link}
            className="text-dark me-3"
            onClick={() => {
              console.log(
                heroDarkButtonItem?.link,
                "heroDarkButtonItem?.link "
              );
              if (heroDarkButtonItem?.link == "/register") {
                storageSrv.reset("session");
                storageSrv.reset("local");
              }
              window.open(heroDarkButtonItem?.link, "_self");
            }}
            style={{
              padding: "10px 50px",
              borderRadius: "50px",
            }}
            bgColor={buttonStyle?.bgColor}
            textColor={buttonStyle?.textColor}
            hoverBgColor={buttonStyle?.hoverBgColor}
            hoverTextColor={buttonStyle?.hoverTextColor}
          >
            {heroDarkButtonItem?.name}
          </S.GButton>
        )}
        {heroLightButtonItem && (
          <Button
            variant="outline-secondary"
            as={HashLink}
            to={heroLightButtonItem?.link}
            className="d-flex align-items-center"
          >
            {heroLightButtonItem?.name}
          </Button>
        )}
      </div>
    );
  };
  const _renderBrandIf = () => {
    return (
      <div className="text-center">
        {heroBrand?.logo && (
          <a href={heroBrand?.link} target="_blank" rel="noopener noreferrer">
            <Image
              src={heroBrand?.logo}
              height={heroBrand?.height ?? 25}
              width={heroBrand?.width ?? 25}
              className="mb-3"
              alt={heroBrand.name}
            />
            {heroBrand?.footerText && (
              <p className="text-muted font-small m-0">
                {heroBrand.footerText}
              </p>
            )}
          </a>
        )}
      </div>
    );
  };

  const _renderOrgImageIf = () => {
    if (heroOrg?.image) {
      return (
        <div style={{ position: "absolute", top: "2%", left: "1%" }}>
          <Image
            src={heroOrg?.image}
            height={heroOrg?.height ?? 35}
            width={heroOrg?.width ?? 35}
            alt={heroOrg?.name ?? "org"}
            layout="fill"
            objectFit="contain"
          />
        </div>
      );
    }
  };

  return (
    <S.Section
      className="section-header pt-5 pt-lg-6 pb-9 pb-lg-12 overflow-hidden"
      id={heroSectionId}
      bgImg={
        isMobileView && heroWatermark?.mobileImage
          ? heroWatermark.mobileImage
          : heroWatermark?.image
      }
      height={heroWatermark?.height}
    >
      <Container>
        {_renderOrgImageIf()}
        <Row>
          <Col xs={12} className="text-center">
            {_renderHeadingIf()}
            {_renderBrandIf()}
            {/* {_renderDescriptionIf()} */}
            {_renderDescriptionButtonsIf()}
          </Col>
        </Row>
      </Container>
    </S.Section>
  );
};

HeroSection.defaultProps = {
  heroSectionId: "#home",
  buttonStyle: {
    bgColor: "white",
    textColor: "black",
    hoverBgColor: "#f8f8f8",
    hoverTextColor: "black",
  },
};
